import { useState } from "react";
import { motion } from "framer-motion";
import {
  Newspaper,
  ExternalLink as ExternalLinkIcon,
  Video,
  Presentation,
  Award,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  containerVariants,
  titleVariants,
  textVariants,
} from "../utils/animations";

// Import images
import NYW1 from "../assets/NYW1.JPG";
import NYW2 from "../assets/NYW2.JPG";
import podcastImage from "../assets/podcast.jpg";

// Amazon Logo SVG Component
const AmazonLogo = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 200 200"
    fill="currentColor"
    className="text-white"
    aria-hidden="true"
  >
    <path d="M101.25 12c-24.406 0-51.281 9.25-56.875 39.25C43.578 54.453 46 55.969 48 56.375l24.75 2.375c2.406 0 4.078-2.344 4.875-4.75 2-10.406 10.781-15.625 20.375-15.625 5.203 0 11.172 2.078 14.375 6.875 3.594 5.203 2.875 12.375 2.875 18.375v3.625c-14.797 1.594-34 2.75-48 8.75C51.25 82.797 40 96.828 40 117.625c0 26.406 16.797 39.625 38 39.625 18 0 28-4.469 42-18.875 4.797 6.797 6.344 10.047 14.75 17.25 2 1.203 4.406.719 6-.875v.5c5.203-4.406 14.422-12.844 19.625-17.25 2-1.203 1.594-4 0-6-4.797-6.406-9.625-11.625-9.625-23.625V68.75c0-16.797 1.281-32.406-11.125-44-10-9.594-25.969-12.75-38.375-12.75M108 88h6.75v5.625c0 9.594.422 17.953-4.375 26.75-4 7.203-10.422 11.625-17.625 11.625-9.594 0-15.5-7.172-15.5-18.375C77.25 94.828 92 89.203 108 88m70.375 58.375c-6.406 0-14.031 1.625-19.625 5.625-1.594 1.203-1.5 2.75.5 2.75 6.406-.797 20.703-2.719 23.5.875 2.406 3.203-2.719 16.719-5.125 23.125-.797 2 .781 2.453 2.375 1.25 10.797-9.203 13.625-27.969 11.625-30.375-1.203-2-6.844-3.25-13.25-3.25M8.75 150c-1.203 0-1.969 2.047-.375 3.25 24 21.594 55.656 34.75 91.25 34.75 25.203 0 54.344-7.953 74.75-22.75 3.203-2.406.453-6.469-2.75-4.875-22.797 9.594-47.219 14.375-69.625 14.375-33.203 0-65.25-9.203-91.25-24-.797-.797-1.594-.75-2-.75" />
  </svg>
);

// Individual Media Card Component
const MediaCard = ({ media }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
    hover: {
      scale: 1.02,
      y: -5,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const logoVariants = {
    hidden: { opacity: 0, scale: 0, rotate: -90 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2,
      },
    },
  };

  return (
    <motion.a
      href={media.link}
      target="_blank"
      rel="noopener noreferrer"
      className="block no-underline bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer"
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      whileHover="hover"
      aria-label={`${media.title} (öffnet in neuem Tab)`}
    >
      <div className="flex items-center space-x-4">
        <motion.div
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden"
          style={{ backgroundColor: media.bgColor, minWidth: "3.5rem" }}
          variants={logoVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          aria-hidden="true"
        >
          {media.isIcon ? (
            <media.logo size={20} className="sm:w-6 sm:h-6" />
          ) : media.isImage ? (
            <img
              src={media.logo}
              alt={`${media.title} logo`}
              className="w-full h-full object-cover"
            />
          ) : media.isAmazonLogo ? (
            <AmazonLogo />
          ) : (
            <span className="text-sm sm:text-base font-bold">{media.logo}</span>
          )}
        </motion.div>
        <div className="flex-1 min-w-0">
          <motion.h3
            className="text-lg sm:text-xl font-bold text-[#0F1B2D] truncate"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {media.title}
          </motion.h3>
          <motion.div
            className="flex items-center justify-between"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <p className="text-gray-600 text-sm sm:text-base">
              {media.description}
            </p>
            {media.link && (
              <ExternalLinkIcon
                size={16}
                className="text-gray-400 ml-2 flex-shrink-0"
                aria-hidden="true"
              />
            )}
          </motion.div>
        </div>
      </div>
    </motion.a>
  );
};

// Individual NYW Article Component
const NYWArticle = ({ article, index }) => {
  const articleVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: index * 0.2,
      },
    },
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      // Add navigation logic here if needed
    }
  };

  return (
    <motion.div
      className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl flex flex-col"
      variants={articleVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      whileHover={{ y: -5 }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="article"
      aria-labelledby={`article-title-${article.id}`}
    >
      <div className="aspect-[4/5] overflow-hidden">
        <img
          src={article.image}
          alt={`${article.title} - Article image`}
          className="w-full h-full object-cover object-top group-hover:scale-102 transition-transform duration-500"
        />
      </div>
      <div className="p-6 bg-[#0F1B2D] flex-1 flex flex-col">
        <div
          className="flex items-center gap-2 text-sm text-[#C5A66A] mb-3"
          aria-hidden="true"
        >
          <Newspaper size={14} />
          <span>{article.date}</span>
        </div>
        <h3
          id={`article-title-${article.id}`}
          className="text-xl font-bold text-white mb-3 group-hover:text-[#C5A66A] transition-colors"
        >
          {article.title}
        </h3>
        <p className="text-gray-300 mb-2 leading-relaxed flex-1">
          {article.description}
        </p>
      </div>
    </motion.div>
  );
};

// YouTube-Facade: Bis zum Klick wird NICHTS von Google geladen - kein iframe,
// kein Thumbnail von i.ytimg.com. Der Platzhalter ist reines lokales Markup.
// Erst der Klick bindet das nocookie-iframe ein; dieser aktive Klick ist die
// Rechtsgrundlage (Art. 6 Abs. 1 lit. a DSGVO), unabhaengig vom Cookie-Banner.
const VideoFacade = ({ videoId, title, t }) => {
  const [activated, setActivated] = useState(false);

  if (activated) {
    return (
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
        title={title}
        className="w-full h-full"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
    );
  }

  const activate = () => setActivated(true);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${t("media.video.play")}: ${title}`}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
      className="group w-full h-full bg-[#0F1B2D] flex flex-col items-center justify-center gap-4 px-6 text-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A66A] focus-visible:ring-inset"
    >
      <span
        className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C5A66A] to-[#d4b886] flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 24 24"
          className="w-7 h-7 ml-1 text-[#0F1B2D]"
          fill="currentColor"
        >
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
      <span className="text-white font-semibold">{t("media.video.play")}</span>
      <span className="text-gray-400 text-xs max-w-md leading-relaxed">
        {t("media.video.consentHint")}
      </span>
    </div>
  );
};

const VideoBlock = ({ videoId, title, t }) => (
  <motion.div
    className="mb-16"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, ease: "easeOut" }}
  >
    <div className="max-w-4xl mx-auto">
      <div className="aspect-video bg-white rounded-xl shadow-lg overflow-hidden">
        <VideoFacade videoId={videoId} title={title} t={t} />
      </div>
    </div>
  </motion.div>
);

// NYW Articles Component
const NYWArticles = ({ t }) => {
  const articles = [
    {
      id: 1,
      image: NYW1,
      title: t("media.articles.article1.title"),
      date: t("media.articles.newYorkWeekly"),
      description: t("media.articles.article1.description"),
    },
    {
      id: 2,
      image: NYW2,
      title: t("media.articles.article2.title"),
      date: t("media.articles.newYorkWeekly"),
      description: t("media.articles.article2.description"),
    },
  ];

  return (
    <div className="mb-16">
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-8"
        role="grid"
        aria-label="News articles grid"
      >
        {articles.map((article, index) => (
          <div key={article.id} role="gridcell">
            <NYWArticle article={article} index={index} />
          </div>
        ))}
      </div>
    </div>
  );
};

// Media Section
const MediaSection = () => {
  const { t } = useTranslation();

  const mediaItems = [
    {
      logo: podcastImage,
      title: t("media.podcast.title"),
      description: t("media.podcast.description"),
      bgColor: "#3B82F6",
      isIcon: false,
      isImage: true,
      link: "https://inside-insurance.podigee.io/33-daaniyal-khan",
    },
    {
      title: t("media.book.title"),
      description: t("media.book.description"),
      bgColor: "#FF9900",
      isAmazonLogo: true,
      link: "https://www.amazon.de/stores/author/B0BPJNFFH8",
    },
    {
      title: t("media.businesstalk.title"),
      description: t("media.businesstalk.description"),
      bgColor: "#0F1B2D",
      isIcon: true,
      logo: Video,
      link: "https://www.businesstalk-kudamm.com/tag/daaniyal-khan/",
    },
    {
      title: t("media.amc.title"),
      description: t("media.amc.description"),
      bgColor: "#C5A66A",
      isIcon: true,
      logo: Presentation,
      link: "https://www.amc-forum.de/content/veranstaltung/details.php?id=2584",
    },
    {
      title: t("media.tedx.title"),
      description: t("media.tedx.description"),
      bgColor: "#E62B1E",
      isIcon: true,
      logo: Award,
      link: "https://www.tedxkarlshochschule.de/speakers",
    },
  ];

  return (
    <section className="pt-32 pb-20 bg-[#F5F7FA]" aria-labelledby="media-title">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2
            id="media-title"
            className="font-playfair text-4xl lg:text-5xl font-bold text-[#C5A66A] mb-6"
            variants={titleVariants}
          >
            {t("media.title")}
          </motion.h2>
          <motion.p
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            variants={textVariants}
          >
            {t("media.subtitle")}
          </motion.p>
        </motion.div>

        <NYWArticles t={t} />

        <VideoBlock
          videoId="VArSel45ZKA"
          title="Daaniyal Khan New YouTube Video"
          t={t}
        />

        <VideoBlock
          videoId="GmEw8VNbPCw"
          title="Daaniyal Khan YouTube Video"
          t={t}
        />

        <div className="max-w-2xl mx-auto">
          <div className="space-y-6">
            {mediaItems.map((media, index) => (
              <MediaCard key={index} media={media} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MediaSection;
